import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { ChampsLigne, champsDeLigne, valeursDeLigne } from "@/composants/ligne-fournisseur";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  dateSemaine,
  euros,
  quantiteLisible,
  statutCommandeFournisseur,
  tonStatutCommandeFournisseur,
  typeProblemeFournisseur,
} from "@/lib/libelles";
import {
  api,
  type CommandeFournisseur,
  type LigneFournisseurDuLot,
  type Page,
} from "@/lib/api";

/**
 * La carte « Commandes fournisseurs » de la fiche d'une commande client (LOT 2,
 * ticket #14) : ce qu'on a commandé pour ses lots, chez qui, et ce qui y pose
 * problème (ticket #16) — y compris les commandes fournisseurs qui ne la servent
 * qu'en partie, regroupées avec d'autres commandes client (ticket #37).
 *
 * `peutCommander` combine la permission et `intervenable` (ADR-0032) ; le
 * serveur refuse quoi qu'il en soit.
 */
export function CommandesFournisseurs({
  commandeClientId,
  peutCommander,
  voitLesMontants,
}: {
  commandeClientId: string;
  peutCommander: boolean;
  voitLesMontants: boolean;
}) {
  const { donnees, erreur, enCours, recharger } = useChargement<Page<CommandeFournisseur>>(
    (signal) => api.commandesFournisseurs({ commande_client_id: commandeClientId }, signal),
    [commandeClientId],
  );
  const [creation, setCreation] = useState(false);

  return (
    <Carte>
      <EnTeteCarte className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Commandes fournisseurs</h2>
        {peutCommander && !creation ? (
          <Bouton taille="compact" variante="discret" onClick={() => setCreation(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Nouvelle commande fournisseur
          </Bouton>
        ) : null}
      </EnTeteCarte>

      {creation ? (
        <CorpsCarte className="border-b border-filet">
          <NouvelleCommandeFournisseur
            commandeClientId={commandeClientId}
            voitLesMontants={voitLesMontants}
            onAnnuler={() => setCreation(false)}
          />
        </CorpsCarte>
      ) : null}

      {erreur ? <Erreur message={erreur} onReessayer={recharger} /> : null}
      {!erreur && (enCours || !donnees) ? (
        <Chargement message="Chargement des commandes fournisseurs…" />
      ) : null}
      {donnees && donnees.total === 0 ? (
        <Vide
          titre="Aucune commande fournisseur"
          detail="Les commandes passées chez les fournisseurs pour cette commande client apparaissent ici."
        />
      ) : null}
      {donnees && donnees.elements.length > 0 ? (
        <ul className="divide-y divide-filet">
          {donnees.elements.map((commande) => (
            <li key={commande.id}>
              <Link
                to={`/commandes-fournisseurs/${commande.id}`}
                className="flex flex-col gap-1 px-4 py-3 hover:bg-chene-clair/25 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4"
              >
                <span className="min-w-0 text-sm sm:flex-1 sm:basis-64">
                  <span className="font-medium text-encre">{commande.numero}</span>
                  <span className="text-encre-douce">
                    {" "}
                    — {commande.fournisseur.nom}, commandée le {dateSemaine(commande.date_commande)}
                    {commande.reference_fournisseur ? `, réf. ${commande.reference_fournisseur}` : ""}
                  </span>
                  {commande.commandes_clients.length > 1 ? (
                    <span className="block text-encre-douce">
                      Regroupée avec{" "}
                      {commande.commandes_clients
                        .filter((servie) => servie.id !== commandeClientId)
                        .map((servie) => servie.numero_commande_client)
                        .join(", ")}
                    </span>
                  ) : null}
                </span>
                <span className="text-sm text-encre-douce sm:shrink-0">
                  {commande.date_prevue
                    ? `Prévue le ${dateSemaine(commande.date_prevue)}`
                    : "Date prévue à saisir"}
                </span>
                <span className="text-sm text-encre-douce sm:shrink-0">
                  {commande.nombre_lignes} ligne{commande.nombre_lignes > 1 ? "s" : ""}
                </span>
                {commande.nombre_problemes_ouverts > 0 ? (
                  <span className="sm:shrink-0">
                    <Etiquette ton="alerte">
                      {commande.nombre_problemes_ouverts} problème
                      {commande.nombre_problemes_ouverts > 1 ? "s ouverts" : " ouvert"}
                    </Etiquette>
                  </span>
                ) : null}
                {voitLesMontants ? (
                  <span className="text-sm tabular-nums text-encre sm:shrink-0">
                    {commande.montant_ht === null ? "Non chiffrée" : euros(commande.montant_ht)}
                  </span>
                ) : null}
                <span className="sm:shrink-0">
                  <Etiquette ton={tonStatutCommandeFournisseur(commande.statut)}>
                    {statutCommandeFournisseur(commande.statut)}
                  </Etiquette>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Carte>
  );
}

/**
 * La carte « Lignes fournisseur » de la fiche d'un lot (LOT 2, ticket #15) : ce
 * qui est commandé pour lui, chez qui, et ce qui en est reçu — « la cuisine est
 * arrivée, le dressing non ». Une ligne sans lot n'y paraît pas (ADR-0001).
 * Le lien ouvre la commande fournisseur de la ligne.
 */
export function LignesFournisseurDuLot({
  lotId,
  voitLesMontants,
}: {
  lotId: string;
  voitLesMontants: boolean;
}) {
  const { donnees, erreur, enCours, recharger } = useChargement<LigneFournisseurDuLot[]>(
    (signal) => api.lignesFournisseurDuLot(lotId, signal),
    [lotId],
  );

  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Lignes fournisseur</h2>
      </EnTeteCarte>
      {erreur ? <Erreur message={erreur} onReessayer={recharger} /> : null}
      {!erreur && (enCours || !donnees) ? (
        <Chargement message="Chargement des lignes fournisseur…" />
      ) : null}
      {donnees && donnees.length === 0 ? (
        <Vide
          titre="Rien de commandé pour ce lot"
          detail="Les lignes des commandes fournisseurs destinées à ce lot apparaissent ici."
        />
      ) : null}
      {donnees && donnees.length > 0 ? (
        <ul className="divide-y divide-filet">
          {donnees.map((ligne) => (
            <li key={ligne.id}>
              <Link
                to={`/commandes-fournisseurs/${ligne.commande_fournisseur_id}`}
                className="flex flex-col gap-1 px-4 py-3 hover:bg-chene-clair/25 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4"
              >
                <span className="min-w-0 text-sm sm:flex-1 sm:basis-64">
                  <span className="font-medium text-encre">{ligne.designation}</span>
                  {ligne.reference ? (
                    <span className="text-encre-douce"> — réf. {ligne.reference}</span>
                  ) : null}
                  <span className="block text-encre-douce">
                    Chez {ligne.fournisseur.nom}
                    {/* Sans référence du fournisseur, la commande se nomme par sa date. */}
                    {ligne.reference_fournisseur
                      ? `, commande ${ligne.reference_fournisseur}`
                      : `, commande du ${dateSemaine(ligne.date_commande)}`}
                  </span>
                  {/* Ticket #16 : ce qui ne va pas sur cette ligne, ou sur toute sa
                      commande, pèse sur ce lot. */}
                  {ligne.problemes_ouverts.map((probleme) => (
                    <span key={probleme.id} className="mt-1 block text-alerte">
                      {probleme.decision === "MAINTENUE"
                        ? "Non-conformité maintenue, en attente"
                        : probleme.designation_ligne
                          ? "Problème ouvert"
                          : "Problème ouvert sur la commande"}{" "}
                      : {typeProblemeFournisseur(probleme.type)} — {probleme.description}
                    </span>
                  ))}
                </span>
                <span className="text-sm tabular-nums text-encre sm:shrink-0">
                  Reçu {quantiteLisible(ligne.quantite_recue)} sur{" "}
                  {quantiteLisible(ligne.quantite_commandee)} {ligne.unite}
                </span>
                <span className="text-sm text-encre-douce sm:shrink-0">
                  {ligne.date_prevue
                    ? `Prévue le ${dateSemaine(ligne.date_prevue)}`
                    : "Date prévue à saisir"}
                </span>
                {voitLesMontants ? (
                  <span className="text-sm tabular-nums text-encre-douce sm:shrink-0">
                    {ligne.prix_unitaire_ht === null
                      ? "Sans prix"
                      : `${euros(ligne.prix_unitaire_ht)} l'unité`}
                  </span>
                ) : null}
                <span className="sm:shrink-0">
                  <Etiquette ton={tonStatutCommandeFournisseur(ligne.statut_commande)}>
                    {statutCommandeFournisseur(ligne.statut_commande)}
                  </Etiquette>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Carte>
  );
}

/**
 * La commande en préparation naît ici, avec ses premières lignes (ADR-0044). Chacune sert
 * d'office cette commande client ; une autre du même propriétaire se choisit
 * ligne à ligne, pour atteindre le franco (ticket #37) — de tout propriétaire,
 * pour qui porte « Regrouper les approvisionnements de plusieurs chargés
 * d'affaires » (ticket #45) : l'API le propose. Seuls les fournisseurs
 * actifs sont proposés — la base refuserait les autres, et dit pourquoi si l'un
 * change de statut entre-temps.
 */
function NouvelleCommandeFournisseur({
  commandeClientId,
  voitLesMontants,
  onAnnuler,
}: {
  commandeClientId: string;
  voitLesMontants: boolean;
  onAnnuler: () => void;
}) {
  const naviguer = useNavigate();
  const fournisseurs = useChargement(
    (signal) => api.fournisseurs({ statut: "ACTIF", limite: 200 }, signal),
    [],
  );
  const proposees = useChargement(
    (signal) => api.commandesProposees({ commande_client_id: commandeClientId }, signal),
    [commandeClientId],
  );
  const [fournisseurId, setFournisseurId] = useState("");
  const [reference, setReference] = useState("");
  const [dateDemandee, setDateDemandee] = useState("");
  // Chaque ligne de la commande en préparation garde sa clé : en retirer une ne mêle
  // pas les saisies.
  const [prochaineCle, setProchaineCle] = useState(1);
  const [lignes, setLignes] = useState(() => [
    { cle: 0, valeurs: valeursDeLigne(commandeClientId) },
  ]);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const chargement = fournisseurs.erreur ?? proposees.erreur;
  if (chargement) {
    return (
      <Erreur
        message={chargement}
        onReessayer={() => {
          fournisseurs.recharger();
          proposees.recharger();
        }}
      />
    );
  }
  if (!fournisseurs.donnees || !proposees.donnees) {
    return <Chargement message="Chargement des fournisseurs…" />;
  }
  const actifs = fournisseurs.donnees.elements;
  if (actifs.length === 0) {
    return (
      <Vide
        titre="Aucun fournisseur actif"
        detail="Créez d'abord la fiche du fournisseur, depuis la liste des fournisseurs."
      />
    );
  }
  const choisi = actifs.find((f) => f.id === fournisseurId);
  const commandes = proposees.donnees;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setRefus(null);
        setEnvoi(true);
        try {
          const creee = await api.creerCommandeFournisseur({
            commande_client_id: commandeClientId,
            fournisseur_id: fournisseurId,
            reference_fournisseur: reference.trim() || null,
            date_demandee: dateDemandee || null,
            lignes: lignes.map((ligne) => champsDeLigne(ligne.valeurs, voitLesMontants)),
          });
          naviguer(`/commandes-fournisseurs/${creee.id}`);
        } catch (cause) {
          setRefus(messageDe(cause));
          setEnvoi(false);
        }
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <ChampSelection
          libelle="Fournisseur"
          required
          options={[
            { valeur: "", libelle: "Choisir un fournisseur" },
            ...actifs.map((f) => ({ valeur: f.id, libelle: f.nom })),
          ]}
          value={fournisseurId}
          onChange={(e) => setFournisseurId(e.target.value)}
        />
        <Champ
          libelle="Référence chez le fournisseur"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
        <Champ
          libelle="Date demandée"
          type="date"
          aide={
            choisi?.delai_moyen_jours != null
              ? `Laissée vide, elle est proposée à ${choisi.delai_moyen_jours} jours, le délai moyen du fournisseur.`
              : "Facultative : elle se corrige sur la commande."
          }
          value={dateDemandee}
          onChange={(e) => setDateDemandee(e.target.value)}
        />
      </div>
      {lignes.map((ligne, rang) => (
        <div
          key={ligne.cle}
          role="group"
          aria-label={`Ligne ${rang + 1}`}
          className="flex flex-col gap-3 border-t border-filet pt-3"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium text-encre">Ligne {rang + 1}</h3>
            {lignes.length > 1 ? (
              <Bouton
                type="button"
                taille="compact"
                variante="discret"
                aria-label={`Retirer la ligne ${rang + 1}`}
                onClick={() => setLignes(lignes.filter((autre) => autre.cle !== ligne.cle))}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Retirer
              </Bouton>
            ) : null}
          </div>
          <ChampsLigne
            valeurs={ligne.valeurs}
            onChange={(valeurs) =>
              setLignes(
                lignes.map((autre) => (autre.cle === ligne.cle ? { ...autre, valeurs } : autre)),
              )
            }
            commandes={commandes}
            avecPrix={voitLesMontants}
          />
        </div>
      ))}
      <span>
        <Bouton
          type="button"
          taille="compact"
          variante="discret"
          onClick={() => {
            setLignes([...lignes, { cle: prochaineCle, valeurs: valeursDeLigne(commandeClientId) }]);
            setProchaineCle(prochaineCle + 1);
          }}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Une ligne de plus
        </Bouton>
      </span>
      {refus ? <Erreur message={refus} /> : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !fournisseurId}>
          Créer la commande en préparation
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
